<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const repostTrackings = ref([]);
const isLoading = ref(false);
const filter = ref('pending');
const currentPage = ref(1);
const perPage = 10;

const filteredTrackings = computed(() => {
  if (filter.value === 'pending') {
    return repostTrackings.value.filter(
      r => r.pending_count > 0 || r.status === 'overdue'
    );
  }
  return repostTrackings.value;
});

const paginatedTrackings = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  return filteredTrackings.value.slice(start, start + perPage);
});

const totalEntries = computed(() => filteredTrackings.value.length);

const paginationStart = computed(() => {
  if (totalEntries.value === 0) return 0;
  return (currentPage.value - 1) * perPage + 1;
});

const paginationEnd = computed(() => {
  return Math.min(currentPage.value * perPage, totalEntries.value);
});

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

function statusClass(status) {
  if (status === 'overdue') return 'bg-n-ruby-9 text-white';
  if (status === 'pending') return 'bg-n-amber-9 text-white';
  if (status === 'complete') return 'bg-n-teal-9 text-white';
  return 'bg-n-slate-9 text-white';
}

function statusLabel(status) {
  if (status === 'overdue') return 'Vencido';
  if (status === 'pending') return 'Pendente';
  if (status === 'complete') return 'Completo';
  return status;
}

function compliancePercent(tracking) {
  const total =
    (tracking.reposted_count || 0) +
    (tracking.pending_count || 0) +
    (tracking.dismissed_count || 0);
  if (total === 0) return 0;
  return Math.round(((tracking.reposted_count || 0) / total) * 100);
}

async function fetchRepostTrackings() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getRepostTrackings({
      inboxId: props.selectedInbox.id,
    });
    repostTrackings.value = response.data || [];
  } catch {
    repostTrackings.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function refreshOfficialPosts() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    await InstagramMediaReportsAPI.refreshOfficialPosts({
      inboxId: props.selectedInbox.id,
    });
    setTimeout(fetchRepostTrackings, 3000);
  } catch {
    // silently fail
    isLoading.value = false;
  }
}

watch(
  () => props.selectedInbox,
  () => {
    if (props.selectedInbox) {
      fetchRepostTrackings();
    }
  }
);

onMounted(() => {
  if (props.selectedInbox) {
    fetchRepostTrackings();
  }
});
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <!-- Top bar: Filter + Refresh -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <select
              v-model="filter"
              class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            >
              <option value="pending">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_FILTER_PENDING') }}
              </option>
              <option value="all">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_FILTER_ALL') }}
              </option>
            </select>
          </div>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors"
            @click="refreshOfficialPosts"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_REFRESH_BUTTON') }}
          </button>
        </div>

        <!-- Table -->
        <div class="bg-n-solid-2 rounded-lg border border-n-weak mb-4">
          <div v-if="paginatedTrackings.length" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_POST') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_PUBLISHED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_DEADLINE') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_PENDING') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_REPOSTED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_DISMISSED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_COMPLIANCE') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_STATUS') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_COL_ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="tracking in paginatedTrackings"
                  :key="tracking.id"
                  class="border-b border-n-weak"
                >
                  <td class="py-2 px-3 text-n-slate-12 max-w-xs">
                    <p class="font-medium truncate">
                      {{ tracking.post_text || tracking.media_id }}
                    </p>
                    <p
                      v-if="tracking.caption"
                      class="text-xs text-n-slate-11 truncate"
                    >
                      {{ tracking.caption }}
                    </p>
                  </td>
                  <td class="py-2 px-3 text-center text-xs text-n-slate-11">
                    {{ tracking.published_at ? formatDate(tracking.published_at) : '-' }}
                  </td>
                  <td class="py-2 px-3 text-center text-xs text-n-slate-11">
                    {{ tracking.deadline_at ? formatDate(tracking.deadline_at) : '-' }}
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      v-if="tracking.pending_count"
                      class="inline-flex items-center justify-center px-2 py-0.5 text-xs rounded-full bg-n-ruby-9 text-white font-medium"
                    >
                      {{ tracking.pending_count }}
                    </span>
                    <span v-else class="text-xs text-n-slate-11">0</span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      v-if="tracking.reposted_count"
                      class="inline-flex items-center justify-center px-2 py-0.5 text-xs rounded-full bg-n-teal-9 text-white font-medium"
                    >
                      {{ tracking.reposted_count }}
                    </span>
                    <span v-else class="text-xs text-n-slate-11">0</span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      v-if="tracking.dismissed_count"
                      class="inline-flex items-center justify-center px-2 py-0.5 text-xs rounded-full bg-n-slate-9 text-white font-medium"
                    >
                      {{ tracking.dismissed_count }}
                    </span>
                    <span v-else class="text-xs text-n-slate-11">0</span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <div class="flex items-center gap-2 justify-center">
                      <div class="w-16 h-2 bg-n-slate-3 rounded-full overflow-hidden">
                        <div
                          class="h-full bg-n-teal-9 rounded-full transition-all"
                          :style="{ width: compliancePercent(tracking) + '%' }"
                        />
                      </div>
                      <span class="text-xs text-n-slate-11">
                        {{ compliancePercent(tracking) }}%
                      </span>
                    </div>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full"
                      :class="statusClass(tracking.status)"
                    >
                      {{ statusLabel(tracking.status) }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <a
                      v-if="tracking.permalink"
                      :href="tracking.permalink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs text-n-blue-11 hover:underline"
                    >
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_VIEW_DETAILS') }}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="flex items-center justify-center py-8 text-n-slate-11"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_EMPTY') }}
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalEntries > 0"
          class="text-xs text-n-slate-11 text-center"
        >
          {{ $t('INSTAGRAM_MEDIA_REPORTS.REPOSTS_PAGINATION', {
            start: paginationStart,
            end: paginationEnd,
            total: totalEntries,
          }) }}
        </div>
      </template>
    </template>
  </div>
</template>
